/**
 * Usage Chart component for displaying usage data in different formats
 * 
 * Supports pie charts, bar charts, and line charts.
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// Chart colors palette
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'];

// Maximum characters for label truncation
const MAX_LABEL_LENGTH = 20;

interface UsageChartProps<T> {
  data: T[];
  dataKey: string;
  nameKey: string;
  type: 'pie' | 'bar' | 'line';
  height?: number;
  formatValue?: (value: number) => string;
  secondaryDataKey?: string;
}

export function UsageChart<T extends Record<string, unknown>>({
  data,
  dataKey,
  nameKey,
  type,
  height = 300,
  formatValue,
  secondaryDataKey,
}: UsageChartProps<T>) {
  const chartData = useMemo(() => {
    return data.map((item, index) => {
      // For dataKey that needs computing (like 'total_tokens')
      let value = item[dataKey];
      if (dataKey === 'total_tokens') {
        const inputTokens = Number(item['input_tokens'] || 0);
        const outputTokens = Number(item['output_tokens'] || 0);
        value = inputTokens + outputTokens;
      }
      
      const labelText = String(item[nameKey]);
      return {
        name: labelText.length > MAX_LABEL_LENGTH 
          ? labelText.slice(0, MAX_LABEL_LENGTH) + '...'
          : labelText,
        value: Number(value || 0),
        secondaryValue: secondaryDataKey ? Number(item[secondaryDataKey] || 0) : undefined,
        fill: COLORS[index % COLORS.length],
      };
    });
  }, [data, dataKey, nameKey, secondaryDataKey]);

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg p-3 shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm text-muted-foreground">
              {entry.name}: {formatValue ? formatValue(entry.value) : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        No data available
      </div>
    );
  }

  if (type === 'pie') {
    return (
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              labelLine={{ stroke: '#888' }}
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'line') {
    return (
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#8884d8" 
              name="Tokens"
              strokeWidth={2}
            />
            {secondaryDataKey && (
              <Line 
                type="monotone" 
                dataKey="secondaryValue" 
                stroke="#82ca9d" 
                name="Requests"
                strokeWidth={2}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Default: Bar chart
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="value" fill="#8884d8" name="Tokens" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
